/**
 * Thin API client (SPEC.md §5).
 *
 * Session cookies + CSRF, so nothing key-shaped is ever stored where a script
 * on another tab could reach it. Note what is absent: no endpoint here takes a
 * passphrase or a derived key, and there is no code path that could add one.
 */

import { blobUrl, matchBlob, putBlob } from './blobCache';
import { withOfflineFallback } from './offlineCache';
import type { ItemKind, RawItem } from './types';
import { b64decode, b64encode } from './crypto';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`HTTP ${status}: ${detail}`);
    this.name = 'ApiError';
  }
}

/** A PATCH lost the optimistic-concurrency race; reload and reapply. */
export class ConflictError extends ApiError {
  constructor(readonly current: number) {
    super(409, `version conflict (server is at ${current})`);
    this.name = 'ConflictError';
  }
}

/**
 * The backend sets `CSRF_COOKIE_NAME = "peepshow_csrftoken"`, not Django's
 * default. Both names are tried, but neither is load-bearing: if the cookie
 * cannot be found under either, we take the token from the response body of
 * `/api/auth/csrf/` instead.
 *
 * This mattered. Reading only `csrftoken` meant every POST, PATCH, and DELETE
 * from the browser got a 403 while every GET succeeded — so the library
 * rendered perfectly and nothing could be saved.
 */
const CSRF_COOKIE_NAMES = ['peepshow_csrftoken', 'csrftoken'];

export function readCookie(name: string, jar: string): string | null {
  for (const part of jar.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return null;
}

function cookieToken(): string | null {
  const jar = typeof document === 'undefined' ? '' : document.cookie;
  for (const name of CSRF_COOKIE_NAMES) {
    const found = readCookie(name, jar);
    if (found) return found;
  }
  return null;
}

/**
 * Django rejects an unsafe request without a CSRF token, and the SPA's very
 * first request is often a POST to /login/.
 */
async function csrfToken(): Promise<string> {
  const existing = cookieToken();
  if (existing) return existing;

  const response = await fetch('/api/auth/csrf/', { credentials: 'include' });
  // Prefer the body: it is immune to the cookie being renamed, HttpOnly, or
  // partitioned away by the browser.
  try {
    const body = (await response.json()) as { csrftoken?: string };
    if (body?.csrftoken) return body.csrftoken;
  } catch {
    /* fall through to the cookie */
  }
  const token = cookieToken();
  if (!token) {
    throw new ApiError(0, 'Could not obtain a CSRF token; writes will be rejected.');
  }
  return token;
}

interface RequestOptions {
  json?: unknown;
  body?: BodyInit;
  headers?: Record<string, string>;
  raw?: boolean;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  let body = opts.body;
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.json);
  }
  if (method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRFToken'] = await csrfToken();
  }

  const response = await fetch(path, { method, headers, body, credentials: 'include' });

  if (!response.ok) {
    let detail = response.statusText;
    let payload: Record<string, unknown> | null = null;
    try {
      payload = (await response.json()) as Record<string, unknown>;
      detail = typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload);
    } catch {
      /* non-JSON error body; statusText is all we have */
    }
    if (response.status === 409) {
      throw new ConflictError(Number(payload?.current ?? 0));
    }
    throw new ApiError(response.status, detail);
  }

  if (opts.raw) return new Uint8Array(await response.arrayBuffer()) as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface Me {
  username: string;
  salt_b64: string;
}

export interface BlobRef {
  uuid: string;
  size: number;
}

export interface WarmResult {
  bytes: number;
  /** False when the cache write failed, e.g. on quota exhaustion. */
  stored: boolean;
}

export interface Usage {
  used_bytes: number;
  quota_bytes: number;
  blob_count: number;
  item_count: number;
  /** Latest item mtime, or null for an empty vault. A cheap change signal. */
  last_modified: string | null;
}

export const api = {
  login(username: string, password: string): Promise<Me> {
    return request<Me>('POST', '/api/auth/login/', { json: { username, password } });
  },

  logout(): Promise<void> {
    return request<void>('POST', '/api/auth/logout/');
  },

  /** Cached, so a locked vault can still be opened with no network. */
  me(): Promise<Me> {
    return withOfflineFallback('me', () => request<Me>('GET', '/api/auth/me/'));
  },

  register(username: string, password: string, saltB64: string): Promise<Me> {
    return request<Me>('POST', '/api/auth/register/', {
      json: { username, password, salt_b64: saltB64 },
    });
  },

  /**
   * Cached, so the library survives going offline.
   *
   * The payloads are sealed, so this stores nothing the server does not
   * already hold in the same form.
   */
  listItems(kind?: ItemKind | ItemKind[]): Promise<RawItem[]> {
    const q = kind ? `?kind=${Array.isArray(kind) ? kind.join(',') : kind}` : '';
    return withOfflineFallback(`items${q}`, () =>
      request<RawItem[]>('GET', `/api/items/${q}`),
    );
  },

  createItem(kind: ItemKind, payload: Uint8Array): Promise<RawItem> {
    return request<RawItem>('POST', '/api/items/', {
      json: { kind, payload_b64: b64encode(payload) },
    });
  },

  patchItem(uuid: string, payload: Uint8Array, version: number): Promise<RawItem> {
    return request<RawItem>('PATCH', `/api/items/${uuid}/`, {
      json: { payload_b64: b64encode(payload), version },
    });
  },

  deleteItem(uuid: string): Promise<void> {
    return request<void>('DELETE', `/api/items/${uuid}/`);
  },

  /**
   * Raw ciphertext, served from the cache when possible and stored when not.
   *
   * Reading from the cache is what makes preloading worth doing. Writing on
   * the way through is what makes *watching* worth doing: play a video once
   * and every segment it pulled is on disk, so the second viewing needs no
   * network at all. Reconciliation decides afterwards which copies to keep.
   */
  async getBlob(
    uuid: string,
    signal?: AbortSignal,
    /** Told whether this blob came from the cache. The only honest source. */
    onSource?: (cached: boolean) => void,
  ): Promise<Uint8Array> {
    const hit = await matchBlob(uuid);
    if (hit) {
      onSource?.(true);
      return new Uint8Array(await hit.arrayBuffer());
    }
    onSource?.(false);

    const response = await fetch(blobUrl(uuid), { credentials: 'include', signal });
    if (!response.ok) throw new ApiError(response.status, response.statusText);
    const bytes = new Uint8Array(await response.clone().arrayBuffer());
    // Deliberately not awaited: playback must not wait on a cache write, and a
    // quota failure here is not worth failing a fragment over.
    void putBlob(uuid, response);
    return bytes;
  },

  /**
   * Fetch a blob into the queryable cache and report its size.
   *
   * Nothing is decrypted and nothing is retained in memory — the ciphertext
   * goes straight into Cache Storage, where `cachedUuids` can later count it.
   * Already-cached blobs cost nothing, so re-running preload resumes rather
   * than refetching.
   */
  async warmBlob(uuid: string, signal?: AbortSignal): Promise<WarmResult> {
    const hit = await matchBlob(uuid);
    if (hit) return { bytes: Number(hit.headers.get('content-length') ?? 0), stored: true };

    const response = await fetch(blobUrl(uuid), { credentials: 'include', signal });
    if (!response.ok) throw new ApiError(response.status, response.statusText);
    // put() consumes the body, so clone before measuring.
    const bytes = (await response.clone().arrayBuffer()).byteLength;
    // `stored` can be false on a quota failure. The caller must not then claim
    // the blob is cached, or the UI would show progress that does not exist.
    return { bytes, stored: await putBlob(uuid, response) };
  },

  deleteBlob(uuid: string): Promise<void> {
    return request<void>('DELETE', `/api/blobs/${uuid}/`);
  },

  usage(): Promise<Usage> {
    return request<Usage>('GET', '/api/usage/');
  },
};

export function decodePayload(item: RawItem): Uint8Array {
  return b64decode(item.payload_b64);
}
